<template>
  <!--
    UpgradeContent — el cuerpo del upsell a Cohort: hero + comparación de planes +
    "lo que se abre" + CTA de cierre.

    Lo consumen dos superficies sin duplicar contenido:
      · `/upgrade` (la página, que además pone su nav y el footer)
      · `UpgradeOverlay` (la capa sobre la app, que abren los items con candado)

    NO trae el `.upgrade` de raíz: esa clase aporta el fondo y la tipografía, y la pone
    cada consumidor. Las clases de sección son planas, así que funcionan en los dos lados.

    ── Pasada de DS ──────────────────────────────────────────────────────────────────
    El CONTENIDO no cambió: mismas claves de i18n, mismos precios, mismos items, mismo
    orden. Lo que cambió es cómo está construido.

    Los dos tiles de plan ahora son el `ProductCard` DEL WEBSITE, no cards a mano: misma
    anatomía (`.card.product-card` + `.pl-head` + `.pl-divider` + `.price-block` +
    `.feature-list` + `.pl-actions` + `.pl-note`) y el MISMO CSS —`card.css`,
    `price-block.css`, `feature-list.css` del foundation, importados en `main.js`—. Con
    eso vienen las dos variantes del sitio, que además arreglan la jerarquía: antes los
    dos planes eran cards blancos idénticos y el recomendado solo se distinguía por una
    pastilla verde.

      · launchpad → familia content, `.sunken.bordered` (el plan que YA tienes: hundido)
      · cohort    → familia immersive (`data-surface`), check en beacon (el que se ofrece)
  -->
  <div class="upgrade-content">
    <!-- ════════════════════════════════════════════════════════════════
         HERO
         ════════════════════════════════════════════════════════════════ -->
    <!-- Era un gradiente verde de marketing (tres paradas de `$primary-*`) que no está
         en la paleta. Pasa a superficie immersive declarada: el mismo slate de todos los
         heroes de la app, con la cascada de tokens dada vuelta. -->
    <section class="upgrade-hero" data-surface="immersive">
      <div class="upgrade-hero--inner">
        <span class="upgrade-hero--eyebrow">
          <PhSparkle :size="16" weight="regular" />
          {{ $t('upgrade.hero.eyebrow') }}
        </span>
        <h1 class="upgrade-hero--title">{{ $t('upgrade.hero.title') }}</h1>
        <p class="upgrade-hero--lead">{{ $t('upgrade.hero.lead') }}</p>
      </div>
    </section>

    <!-- ════════════════════════════════════════════════════════════════
         COMPARISON — el plan actual vs el escalón siguiente
         ════════════════════════════════════════════════════════════════ -->
    <section class="upgrade-compare">
      <div class="card-grid" data-cols="2">

        <!-- El plan ACTUAL — cualquiera de los tres. Superficie hundida: es el
             que ya tienes, no el que se ofrece. -->
        <article class="card product-card sunken bordered" :class="current.sku">
          <div class="pl-head">
            <p class="pl-tag">{{ $t('upgrade.currentPlan') }}</p>
            <p class="pl-name">{{ current.name }}</p>
            <p class="pl-desc">{{ $t(current.descKey) }}</p>
          </div>

          <div class="pl-divider"></div>

          <div v-if="current.price" class="price-block">
            <span class="price-amount">{{ current.price }}</span>
            <span class="price-unit">{{ $t('upgrade.oneTime') }}</span>
          </div>

          <!-- El `feature-list` del DS solo modela ítems INCLUIDOS (check). Los excluidos
               son propios de una comparación, así que `is-excluded` es un modifier local:
               tacha el texto a muted y cambia el glifo. Anotado como candidato del DS. -->
          <ul class="feature-list">
            <li v-for="(key, idx) in current.features" :key="idx">
              <span class="feature-check"><PhCheck :size="24" weight="regular" /></span>
              <span>{{ $t(key) }}</span>
            </li>
            <li v-for="(key, idx) in current.excluded" :key="`x-${idx}`" class="is-excluded">
              <span class="feature-check"><PhX :size="24" weight="regular" /></span>
              <span>{{ $t(key) }}</span>
            </li>
          </ul>
        </article>

        <!-- El escalón SIGUIENTE: launchpad para un baseline, cohort para un
             launchpad. Antes era Cohort fijo, que desde baseline salteaba un plan. -->
        <article class="card product-card" :class="next.sku" data-surface="immersive">
          <ds-badge class="pl-flag">{{ $t('upgrade.recommended') }}</ds-badge>

          <div class="pl-head">
            <p class="pl-tag is-accent">{{ $t('upgrade.upgradeTo') }}</p>
            <p class="pl-name">{{ next.name }}</p>
            <p class="pl-desc">{{ $t(next.descKey) }}</p>
          </div>

          <div class="pl-divider"></div>

          <div v-if="next.price" class="price-block">
            <span class="price-amount">{{ next.price }}</span>
            <span class="price-unit">{{ $t('upgrade.oneTime') }}</span>
          </div>

          <!-- `beacon` = check en accent-active, la variante del tile ofrecido en el
               sitio. `bordered-end` porque abajo sigue el CTA (regla de card.md). -->
          <ul class="feature-list beacon bordered-end">
            <li v-for="(key, idx) in next.features" :key="idx">
              <span class="feature-check"><PhCheck :size="24" weight="regular" /></span>
              <span>{{ $t(key) }}</span>
            </li>
          </ul>

          <div class="pl-actions">
            <ds-button variant="feature" @click="goToPurchase">
              {{ $t('upgrade.ctaTo', { plan: next.name }) }}
              <template #trailing><PhArrowRight :size="16" weight="regular" /></template>
            </ds-button>
          </div>
          <p class="pl-note">{{ $t('upgrade.ctaMeta') }}</p>
        </article>

      </div>
    </section>

    <!-- ════════════════════════════════════════════════════════════════
         WHAT YOU UNLOCK
         ════════════════════════════════════════════════════════════════ -->
    <!-- Cuatro capacidades: por el algoritmo del playbook del DS (marker + título +
         prosa, sin precio) esto es `Card`, no `ProductCard` — el price-led es solo el
         tile de plan. Van en el shell `.card` con sus helpers y en un `.card-grid`. -->
    <section class="upgrade-unlocks">
      <header class="upgrade-section--head">
        <h2 class="upgrade-section--title">{{ $t('upgrade.unlocks.title') }}</h2>
        <p class="upgrade-section--desc">{{ $t('upgrade.unlocks.desc') }}</p>
      </header>

      <div class="card-grid" data-cols="auto">
        <article class="card sm bordered">
          <span class="card-icon"><PhChartBar :size="32" weight="light" /></span>
          <h3 class="card-title">{{ $t('upgrade.unlocks.monitor.title') }}</h3>
          <p class="card-body">{{ $t('upgrade.unlocks.monitor.desc') }}</p>
        </article>
        <article class="card sm bordered">
          <span class="card-icon"><PhShareNetwork :size="32" weight="light" /></span>
          <h3 class="card-title">{{ $t('upgrade.unlocks.network.title') }}</h3>
          <p class="card-body">{{ $t('upgrade.unlocks.network.desc') }}</p>
        </article>
        <article class="card sm bordered">
          <span class="card-icon"><PhFlag :size="32" weight="light" /></span>
          <h3 class="card-title">{{ $t('upgrade.unlocks.actionPlan.title') }}</h3>
          <p class="card-body">{{ $t('upgrade.unlocks.actionPlan.desc') }}</p>
        </article>
        <article class="card sm bordered">
          <span class="card-icon"><PhUsersThree :size="32" weight="light" /></span>
          <h3 class="card-title">{{ $t('upgrade.unlocks.community.title') }}</h3>
          <p class="card-body">{{ $t('upgrade.unlocks.community.desc') }}</p>
        </article>
      </div>
    </section>

    <!-- ════════════════════════════════════════════════════════════════
         CLOSING CTA
         ════════════════════════════════════════════════════════════════ -->
    <section class="upgrade-closing">
      <div class="card lg elevated bordered upgrade-closing--card">
        <span class="card-icon"><PhSparkle :size="32" weight="light" /></span>
        <h2 class="card-title">{{ $t('upgrade.closing.title') }}</h2>
        <p class="card-body">{{ $t('upgrade.closing.desc') }}</p>
        <div class="upgrade-closing--actions">
          <!-- Este CTA vende COHORT, no el escalón siguiente: es el cierre de la
               sección "What you unlock with Cohort" y el salto aspiracional de la
               página. Por eso `goToCohort` y no `goToPurchase` — ese manda a
               `next.sku`, que desde baseline es launchpad, y el botón terminaba
               diciendo Cohort mientras llevaba a /purchase?product=launchpad.
               El paso incremental ya lo ofrece el CTA de la comparación, arriba. -->
          <ds-button variant="primary" @click="goToCohort">
            {{ $t('upgrade.closing.primary') }}
            <template #trailing><PhArrowRight :size="16" weight="regular" /></template>
          </ds-button>
          <ds-link :href="CALENDLY.expertCall" target="_blank" rel="noopener">
            {{ $t('upgrade.closing.secondary') }}
          </ds-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useSubscription } from '@/composables/useSubscription';
import { nextTier } from '@/config/plans';
import DsButton from '@/components/ds/DsButton.vue';
import DsBadge from '@/components/ds/DsBadge.vue';
import DsLink from '@/components/ds/DsLink.vue';
import {
  PhSparkle, PhCheck, PhX, PhArrowRight, PhChartBar, PhShareNetwork, PhFlag, PhUsersThree,
} from '@phosphor-icons/vue';
import { CALENDLY } from '@/config/contact';

const LAUNCHPAD_FEATURES = [
  'upgrade.features.platform',
  'upgrade.features.assessment',
  'upgrade.features.actionPlanLimited',
  'upgrade.features.onboarding',
  'upgrade.features.emailSupport',
];

const COHORT_ONLY_FEATURES = [
  'upgrade.features.monitor',
  'upgrade.features.network',
  'upgrade.features.expert',
  'upgrade.features.mentorship',
];

const COHORT_FEATURES = [
  'upgrade.features.everythingLaunchpad',
  'upgrade.features.monitor',
  'upgrade.features.network',
  'upgrade.features.expert',
  'upgrade.features.mentorship',
  'upgrade.features.evidenceReview',
  'upgrade.features.inPersonCohort',
];

const BASELINE_FEATURES = [
  'upgrade.features.assignedAssessments',
  'upgrade.features.assessmentReport',
];

// Lo que se abre al pasar de baseline a launchpad.
const LAUNCHPAD_ONLY_FEATURES = [
  'upgrade.features.platform',
  'upgrade.features.actionPlanLimited',
  'upgrade.features.onboarding',
  'upgrade.features.emailSupport',
];

// ════════════════════════════════════════════════════════════════════════
// Catálogo de planes — la comparación es de DOS tarjetas: el plan que tenés
// y el escalón SIGUIENTE. Con dos tiers eso podía estar hardcodeado
// (launchpad vs cohort); con tres, no: un baseline tiene que ver
// baseline → launchpad, y un launchpad, launchpad → cohort.
//
// `sku` es lo que se manda a /purchase. `excluded` son los ítems tachados en
// la tarjeta del plan actual: lo que te estás perdiendo.
// ════════════════════════════════════════════════════════════════════════
const PLAN_CARDS = {
  baseline: {
    name: 'Baseline',
    sku: 'baseline',
    // ⚠️ PRECIO PENDIENTE — el de baseline todavía no está definido. Sale del
    // BE junto con el producto (fase 5); hasta entonces la tarjeta no muestra
    // bloque de precio en vez de inventar una cifra.
    price: null,
    descKey: 'upgrade.baseline.desc',
    features: BASELINE_FEATURES,
    excluded: LAUNCHPAD_ONLY_FEATURES,
  },
  launchpad: {
    name: 'Launchpad',
    sku: 'launchpad',
    price: '$1,750',
    descKey: 'upgrade.launchpad.desc',
    features: LAUNCHPAD_FEATURES,
    excluded: COHORT_ONLY_FEATURES,
  },
  cohort: {
    name: 'Cohort',
    sku: 'cohort',
    price: '$6,500',
    descKey: 'upgrade.cohort.desc',
    features: COHORT_FEATURES,
    excluded: [],
  },
};

export default {
  name: 'UpgradeContent',
  components: {
    DsButton, DsBadge, DsLink,
    PhSparkle, PhCheck, PhX, PhArrowRight, PhChartBar, PhShareNetwork, PhFlag, PhUsersThree,
  },
  // `navigate` avisa que el usuario se va a otra ruta desde adentro del contenido. La
  // página lo ignora; el overlay lo usa para cerrarse y no quedar colgado sobre la
  // pantalla de compra.
  emits: ['navigate'],
  setup(props, { emit }) {
    const router = useRouter();

    const { subscriptionType, tier } = useSubscription();

    // El plan que tenés y el que sigue. `nextTier` da null en cohort y arriba
    // (no hay producto superior comprable hoy); ahí el guard del router ya
    // redirige antes de llegar acá, y el fallback es defensa.
    const current = computed(() => PLAN_CARDS[tier.value] ?? PLAN_CARDS.baseline);
    const next = computed(
      () => PLAN_CARDS[nextTier(subscriptionType.value)] ?? PLAN_CARDS.cohort,
    );

    // Los CTA llevan al escalón SIGUIENTE — antes iban fijo a 'cohort', que
    // desde baseline salteaba launchpad.
    const goToPurchase = () => {
      emit('navigate');
      router.push({ name: 'PurchaseRoute', query: { product: next.value.sku } });
    };

    // El closing CTA apunta SIEMPRE a cohort, sin importar el tier actual. Va como
    // función aparte y no como `goToPurchase(sku)` con default: `@click="fn"` pasa
    // el MouseEvent como primer argumento, así que un parámetro opcional recibiría
    // el evento en vez del sku.
    const goToCohort = () => {
      emit('navigate');
      router.push({ name: 'PurchaseRoute', query: { product: PLAN_CARDS.cohort.sku } });
    };

    return {
      CALENDLY,
      goToPurchase,
      goToCohort,
      current,
      next,
    };
  },
};
</script>
